import React, { useState } from 'react';
import { Coffee, Users, AlertCircle, Plus, Check, X } from 'lucide-react';
import { ConsumoPersonal as ConsumoPersonalType, ConfiguracionConsumo } from '../../types';
import { format } from 'date-fns';

interface ConsumoPersonalProps {
  consumos: ConsumoPersonalType[];
  configuracion: ConfiguracionConsumo;
  onRegistrarConsumo: (consumo: Omit<ConsumoPersonalType, 'id'>) => void;
}

const ConsumoPersonal: React.FC<ConsumoPersonalProps> = ({ 
  consumos, 
  configuracion,
  onRegistrarConsumo 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    empleadoNombre: '',
    bebida: '',
    tipoBebida: 'basico' as 'basico' | 'especialidad' | 'premium',
    cantidad: 1,
    notas: ''
  });

  // Calcular consumos del día actual por empleado
  const today = format(new Date(), 'yyyy-MM-dd');
  const consumosHoy = consumos.filter(c => c.fecha === today);
  
  const consumosPorEmpleado = consumosHoy.reduce((acc, consumo) => {
    if (!acc[consumo.empleadoNombre]) {
      acc[consumo.empleadoNombre] = [];
    }
    acc[consumo.empleadoNombre].push(consumo);
    return acc;
  }, {} as Record<string, ConsumoPersonalType[]>);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const nuevoConsumo = {
      empleadoId: Date.now().toString(),
      empleadoNombre: formData.empleadoNombre,
      fecha: format(new Date(), 'yyyy-MM-dd'),
      hora: format(new Date(), 'HH:mm'),
      bebida: formData.bebida,
      tipoBebida: formData.tipoBebida,
      cantidad: formData.cantidad,
      costo: calcularCosto(formData.tipoBebida),
      autorizado: validarConsumo(formData.empleadoNombre),
      notas: formData.notas
    };

    onRegistrarConsumo(nuevoConsumo);
    setFormData({
      empleadoNombre: '',
      bebida: '',
      tipoBebida: 'basico',
      cantidad: 1,
      notas: ''
    });
    setShowForm(false);
  };

  const calcularCosto = (tipo: string): number => {
    // Costos estimados por tipo de bebida
    switch(tipo) {
      case 'basico': return 25; // Americano, té básico
      case 'especialidad': return 45; // Latte, cappuccino
      case 'premium': return 65; // Bebidas especiales, frappés
      default: return 35;
    }
  };

  const validarConsumo = (empleado: string): boolean => {
    const consumosEmpleado = consumosPorEmpleado[empleado] || [];
    return consumosEmpleado.length < configuracion.limiteDiario;
  };

  const getTipoBebidaColor = (tipo: string) => {
    switch(tipo) {
      case 'basico': return 'bg-green-100 text-green-800';
      case 'especialidad': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-dark-text font-heading">
          Consumo de Personal
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center px-4 py-2 bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Registrar Consumo
        </button>
      </div>

      {/* Estadísticas del día */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Consumos Hoy</p>
              <p className="text-2xl font-bold text-dark-text">
                {consumosHoy.length}
              </p>
            </div>
            <Coffee className="text-accent" size={24} />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Empleados</p>
              <p className="text-2xl font-bold text-dark-text">
                {Object.keys(consumosPorEmpleado).length}
              </p>
            </div>
            <Users className="text-warm-brown" size={24} />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Costo Total</p>
              <p className="text-2xl font-bold text-dark-text">
                ${consumosHoy.reduce((sum, c) => sum + c.costo, 0)}
              </p>
            </div>
            <span className="text-2xl">💰</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-medium-gray">Límite Diario</p>
              <p className="text-2xl font-bold text-dark-text">
                {configuracion.limiteDiario} / empleado
              </p>
            </div>
            <AlertCircle className="text-yellow-500" size={24} />
          </div>
        </div>
      </div>

      {/* Formulario de registro */}
      {showForm && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <h3 className="text-lg font-semibold text-dark-text mb-4">
            Registrar Nuevo Consumo
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-medium-gray mb-1">
                  Empleado
                </label>
                <input
                  type="text"
                  value={formData.empleadoNombre}
                  onChange={(e) => setFormData({...formData, empleadoNombre: e.target.value})}
                  className="w-full px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-medium-gray mb-1">
                  Bebida
                </label>
                <input
                  type="text"
                  value={formData.bebida}
                  onChange={(e) => setFormData({...formData, bebida: e.target.value})}
                  className="w-full px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Ej: Americano, Latte, Cappuccino"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-medium-gray mb-1">
                  Tipo de Bebida
                </label>
                <select
                  value={formData.tipoBebida}
                  onChange={(e) => setFormData({...formData, tipoBebida: e.target.value as any})}
                  className="w-full px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="basico">Básico (Americano, Té)</option>
                  <option value="especialidad">Especialidad (Latte, Cappuccino)</option>
                  <option value="premium">Premium (Frappé, Bebidas especiales)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-medium-gray mb-1">
                  Cantidad
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.cantidad}
                  onChange={(e) => setFormData({...formData, cantidad: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-medium-gray mb-1">
                Notas (opcional)
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => setFormData({...formData, notas: e.target.value})}
                className="w-full px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                rows={2}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-medium-gray hover:text-dark-text transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors"
              >
                Registrar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de consumos del día */}
      <div className="bg-white rounded-lg shadow-sm border border-light-gray/20 overflow-hidden">
        <div className="px-6 py-4 border-b border-light-gray/20">
          <h3 className="text-lg font-semibold text-dark-text font-heading">
            Consumos del Día
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-light-gray/20">
            <thead className="bg-cream">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Empleado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Bebida
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Costo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-light-gray/20">
              {consumosHoy.map((consumo) => (
                <tr key={consumo.id} className="hover:bg-cream/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text">
                    {consumo.hora}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-dark-text">
                      {consumo.empleadoNombre}
                    </div>
                    <div className="text-sm text-medium-gray">
                      {(consumosPorEmpleado[consumo.empleadoNombre] || []).length}/{configuracion.limiteDiario} del día
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text">
                    {consumo.bebida}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTipoBebidaColor(consumo.tipoBebida)}`}>
                      {consumo.tipoBebida}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text">
                    ${consumo.costo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {consumo.autorizado ? (
                      <span className="flex items-center text-green-600">
                        <Check size={16} className="mr-1" />
                        Autorizado
                      </span>
                    ) : (
                      <span className="flex items-center text-red-600">
                        <X size={16} className="mr-1" />
                        Excedido
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {consumosHoy.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-medium-gray">
                    No hay consumos registrados hoy
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen por empleado */}
      {Object.keys(consumosPorEmpleado).length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <h3 className="text-lg font-semibold text-dark-text font-heading mb-4">
            Resumen por Empleado
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(consumosPorEmpleado).map(([empleado, consumos]) => {
              const total = consumos.reduce((sum, c) => sum + c.costo, 0);
              const excedido = consumos.length >= configuracion.limiteDiario;
              
              return (
                <div key={empleado} className={`p-4 rounded-lg border ${
                  excedido ? 'border-red-300 bg-red-50' : 'border-light-gray/20 bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-dark-text">{empleado}</span>
                    {excedido && <AlertCircle className="text-red-500" size={20} />}
                  </div>
                  <div className="text-sm text-medium-gray">
                    <p>Bebidas: {consumos.length} / {configuracion.limiteDiario}</p>
                    <p>Costo total: ${total}</p>
                  </div>
                  <div className="mt-2 space-y-1">
                    {consumos.map((c, idx) => (
                      <div key={idx} className="text-xs text-medium-gray">
                        • {c.hora} - {c.bebida}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumoPersonal;